import React from "react";
import useEditService from "../hooks/useEditService";
import useUploadFiles from "../../../hooks/useUploadFiles";

// components
import Modal from "../../../component/ui/modal";
import Tab from "../../../component/ui/tabs/Tabs";
import Upload from "../../../component/modals/upload";
import TextInput from "../../../component/element/forms/TextInput";
import TextMultipleInput from "../../../component/element/forms/TextMultipleInput";
import ItemAction from "../../../component/ui/action/ItemAction";

// constants

// types and state
import {
  delete_package,
  edit_service,
  get_service,
} from "../../../services/apis/services";
import { useAppDispatch, useAppSelector } from "../../../store";
import { IEditServiceError } from "../../../store/types/service";
import RichTextEditor from "../../../component/element/forms/RichTextEditor";

type IEditService = {
  id: number | string;
  show: boolean;
  onCancel: () => void;
};

const EditService: React.FC<IEditService> = ({ id, show, onCancel }) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(({ service }) => service.loading);
  const { data, err, dataSet, errSet, packageIds, packageIdsSet } =
    useEditService(id);
  const { loader, uploadFile } = useUploadFiles();
  const [deletingPackageId, setDeletingPackageId] = React.useState<
    number | null
  >(null);

  const onLoad = React.useCallback(() => {
    if (id) get_service(dispatch, id);
  }, [dispatch, id]);

  React.useEffect(() => {
    onLoad();
  }, [onLoad]);

  const onsubmit = async () => {
    const errors: IEditServiceError = {
      ...err,
      id: data.id ? "" : "error: this field is required",
      name: data.name.trim() ? "" : "error: this field is required",
      description: data.description.trim()
        ? ""
        : "error: this field is required",
      price: data.price.trim() ? "" : "error: this field is required",
    };
    errSet(errors);
    if (
      !data.id ||
      !data.name.trim() ||
      !data.description.trim() ||
      !data.price.trim()
    ) {
      return;
    }
    const response = await edit_service(data, dispatch);
    if (response) onCancel();
  };

  return (
    <Modal
      title="Edit Service"
      open={show}
      onClose={onCancel}
      onOkClick={onsubmit}
      isLoading={loading}
    >
      <div className="space-y-6">
        <div>
          <Upload
            title="Upload Image"
            name="image"
            status={err.image}
            loading={loader}
            completed={!!data.image}
            imgPreview={data.image}
            onChange={async (fileOrFiles) => {
              const paths = await uploadFile(fileOrFiles as File, "services");
              if (paths.length > 0) {
                const img_path = paths[0]?.base_url + "/" + paths[0]?.path;
                dataSet((prev) => ({ ...prev, image: img_path }));
                errSet((prev) => ({ ...prev, image: "" }));
              }
            }}
          />
        </div>

        <div>
          <TextInput
            value={data.name}
            label="Name"
            name="name"
            placeholder="Service Name"
            type="text"
            status={err.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { name, value } = e.target;
              dataSet((prev) => ({ ...prev, [name]: value }));
              errSet((prev) => ({ ...prev, [name]: "" }));
            }}
          />
        </div>

        <div>
          <TextInput
            value={data.price}
            label="Price"
            name="price"
            placeholder="Service Price"
            type="number"
            status={err.price}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const { value } = e.target;
              dataSet((prev) => ({ ...prev, price: value }));
              errSet((prev) => ({ ...prev, price: "" }));
            }}
          />
        </div>

        <div>
          <div className="mb-6">
            <TextMultipleInput
              label="Packages"
              onChange={(res) => {
                dataSet((prev) => ({
                  ...prev,
                  packages: [...prev.packages, res],
                }));
                packageIdsSet((prev) => [...prev, null]);
                errSet((prev) => ({ ...prev, packages: "" }));
              }}
            />
          </div>
          <div>
            {data.packages.length > 0 && (
              <div className="grid gap-2">
                {data.packages.map((item, index) => {
                  const packageId = packageIds[index];
                  return (
                    <div
                      key={packageId ?? `${item}-${index}`}
                      className="flex items-center justify-between gap-3 border p-2 rounded-lg"
                    >
                      <Tab value={item} />
                      <ItemAction
                        show_view={false}
                        show_edit={false}
                        delete_loading={
                          packageId !== null &&
                          packageId !== undefined &&
                          deletingPackageId === packageId
                        }
                        onView={() => null}
                        onEdit={() => null}
                        onDelete={async () => {
                          if (packageId === null || packageId === undefined) {
                            dataSet((prev) => ({
                              ...prev,
                              packages: prev.packages.filter(
                                (_, itemIndex) => itemIndex !== index,
                              ),
                            }));
                            packageIdsSet((prev) =>
                              prev.filter(
                                (_, itemIndex) => itemIndex !== index,
                              ),
                            );
                            return;
                          }

                          setDeletingPackageId(packageId);
                          try {
                            const deleted = await delete_package(
                              dispatch,
                              packageId,
                            );
                            if (deleted) {
                              dataSet((prev) => ({
                                ...prev,
                                packages: prev.packages.filter(
                                  (_, itemIndex) => itemIndex !== index,
                                ),
                              }));
                              packageIdsSet((prev) =>
                                prev.filter(
                                  (_, itemIndex) => itemIndex !== index,
                                ),
                              );
                            }
                          } finally {
                            setDeletingPackageId(null);
                          }
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <RichTextEditor
          label="Description"
          value={data.description}
          error={err.description}
          onChange={(value) => {
            dataSet((prev) => ({ ...prev, description: value }));
            errSet((prev) => ({ ...prev, description: "" }));
          }}
        />
      </div>
    </Modal>
  );
};

export default EditService;
